import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import { Button, Stack, useTheme } from "@mui/joy";
import React from "react";
import { useNavigate } from "react-router-dom";
import { UniversalSearch } from "./UniversalSearch";
import { PlacePrediction } from "@/api/use-post-google-places-autocomplete";
import { MapToolbarPanel, PLACES_AUTOCOMPLETE_WIDTH } from "./MapToolbarPanel";
import { MapState } from "../hooks/useMapState";
import { useIsMobile } from "../hooks/useIsMobile";
import { BottomNavBar, BottomNavTab } from "./BottomNavBar";
import { BottomSheet, SnapPoint } from "./BottomSheet";

type MapToolbarProps = {
  onSelectPlace: (place: PlacePrediction | null) => void;
  mapState: MapState;
  initialListId?: string | null;
  initialListMode?: "edit" | "view" | null;
};

const BOX_SHADOW = "sm";
const MAP_TOOLBAR_WIDTH = 70;

export const MapToolbar = ({
  onSelectPlace,
  mapState,
  initialListId,
  initialListMode,
}: MapToolbarProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [isPanelOpen, setIsPanelOpen] = React.useState(!!initialListId);

  // Mobile-specific state
  const [activeTab, setActiveTab] = React.useState<BottomNavTab>("saved");
  const [bottomSheetSnapPoint, setBottomSheetSnapPoint] =
    React.useState<SnapPoint>(initialListId ? "half" : "collapsed");
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  // Panel should also be open when a place is clicked on the map
  const shouldShowPanel = isPanelOpen || !!mapState.mapPlaceId;

  // On mobile, open bottom sheet when a place is clicked on the map
  React.useEffect(() => {
    if (isMobile && mapState.mapPlaceId) {
      setBottomSheetSnapPoint("expanded");
      setActiveTab("saved");
    }
  }, [isMobile, mapState.mapPlaceId]);

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    mapState.setMapPlaceId(null);
  };

  const handleMobileSavedClick = () => {
    if (activeTab === "saved" && bottomSheetSnapPoint !== "collapsed") {
      setBottomSheetSnapPoint("collapsed");
    } else {
      setActiveTab("saved");
      setBottomSheetSnapPoint("half");
    }
    setIsSearchOpen(false);
  };

  const handleMobileSearchClick = () => {
    setIsSearchOpen(true);
    setBottomSheetSnapPoint("collapsed");
    setActiveTab("search");
  };

  const handleMobileFeedClick = () => {
    navigate("/feed");
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Search Overlay */}
        {isSearchOpen && (
          <UniversalSearch
            onSelectPlace={(place) => {
              onSelectPlace(place);
              setIsSearchOpen(false);
              if (place) {
                setBottomSheetSnapPoint("half");
                setActiveTab("saved");
              }
            }}
            onClose={() => {
              setIsSearchOpen(false);
              setActiveTab("saved");
            }}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1500,
              p: 1,
              backgroundColor: "background.surface",
              boxShadow: "sm",
            }}
          />
        )}

        {/* Bottom Sheet */}
        <BottomSheet
          snapPoint={bottomSheetSnapPoint}
          onSnapPointChange={setBottomSheetSnapPoint}
        >
          <MapToolbarPanel
            onClose={() => {
              setBottomSheetSnapPoint("collapsed");
              mapState.setMapPlaceId(null);
            }}
            mapState={mapState}
            googlePlaceIdFromMap={mapState.mapPlaceId}
            isMobile
            onExpandRequest={() => setBottomSheetSnapPoint("expanded")}
            initialListId={initialListId}
            initialListMode={initialListMode}
          />
        </BottomSheet>

        {/* Bottom Navigation Bar */}
        <BottomNavBar
          activeTab={activeTab}
          onSavedClick={handleMobileSavedClick}
          onSearchClick={handleMobileSearchClick}
          onFeedClick={handleMobileFeedClick}
        />
      </>
    );
  }

  // Desktop layout — unchanged
  return (
    <>
      <Stack
        direction="row"
        sx={(theme) => ({
          position: "absolute",
          top: 0,
          left: 0,
          boxShadow: theme.shadow[BOX_SHADOW],
        })}
      >
        <Stack
          width={`${MAP_TOOLBAR_WIDTH}px`}
          height="100vh"
          padding={theme.spacing(1)}
          sx={(theme) => ({
            backgroundColor: theme.vars.palette.background.level1,
          })}
        >
          <Button
            onClick={() => setIsPanelOpen(true)}
            variant="plain"
            sx={{ display: "flex", flexDirection: "column" }}
            data-testid="toolbar-saved-button"
          >
            <BookmarkBorderIcon />
            Saved
          </Button>
          <Button
            onClick={() => navigate("/feed")}
            variant="plain"
            sx={{ display: "flex", flexDirection: "column" }}
            data-testid="toolbar-feed-button"
          >
            <DynamicFeedIcon />
            Feed
          </Button>
        </Stack>

        {shouldShowPanel && (
          <MapToolbarPanel
            onClose={handlePanelClose}
            mapState={mapState}
            googlePlaceIdFromMap={mapState.mapPlaceId}
            initialListId={initialListId}
            initialListMode={initialListMode}
          />
        )}
      </Stack>

      <UniversalSearch
        onSelectPlace={onSelectPlace}
        sx={(theme) => ({
          width: `${PLACES_AUTOCOMPLETE_WIDTH}px`,
          position: "absolute",
          top: theme.spacing(2),
          left: `calc(${MAP_TOOLBAR_WIDTH}px + ${theme.spacing(2)})`,
          boxShadow: shouldShowPanel ? undefined : theme.shadow[BOX_SHADOW],
          zIndex: 1500,
        })}
      />
    </>
  );
};
